<script>
  import { Expand, Loader2, Maximize, Minimize, RefreshCw, Shrink, X } from 'lucide-svelte';

  import { onMount, onDestroy } from 'svelte';

  export let open = false;
  export let title = 'Untitled';

  export let onClose = () => {
    open = false;
  };

  export let onConfirm = () => {};
  export let confirmDisabled = false;
  export let confirmBusy = false;
  export let confirmText = 'OK';
  export let cancelText = 'Cancel';

  export let width = '80vw';
  export let height = 'auto';

  export let closeOnOutsideClick = true;

  let modalRef;

  let isDragging = false;
  let isFullscreen = false;
  let isMoved = false;

  let startX = 0;
  let startY = 0;

  let posX = 0;
  let posY = 0;

  let offsetX = 0;
  let offsetY = 0;

  let rafId = null;

  function onMouseDown(event) {
    if (!modalRef || isFullscreen) return;

    isDragging = true;

    startX = event.clientX;
    startY = event.clientY;

    offsetX = posX;
    offsetY = posY;

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  function onMouseMove(event) {
    if (!isDragging) return;

    posX = offsetX + (event.clientX - startX);
    posY = offsetY + (event.clientY - startY);

    if (rafId) return;

    rafId = requestAnimationFrame(() => {
      if (modalRef) {
        modalRef.style.transform = `translate3d(${posX}px, ${posY}px, 0)`;
      }

      rafId = null;
    });
  }

  function onMouseUp() {
    isDragging = false;

    window.removeEventListener('mousemove', onMouseMove);

    window.removeEventListener('mouseup', onMouseUp);

    isMoved = posX !== 0 || posY !== 0;
  }

  function resetPosition() {
    posX = 0;
    posY = 0;

    isMoved = false;

    if (modalRef) {
      modalRef.style.transform = 'translate3d(0px,0px,0px)';
    }
  }

  function toggleFullscreen() {
    isFullscreen = !isFullscreen;

    resetPosition();
  }

  function toggleBrowserFullscreen() {
    if (!modalRef) return;

    if (!document.fullscreenElement) {
      modalRef.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }

  function onFullscreenChange() {
    isFullscreen = !!document.fullscreenElement;
  }

  function handleKeyDown(event) {
    if (!open) return;

    if (event.key === 'Escape') {
      onClose();
    }
  }

  function onBackdropClick() {
    if (closeOnOutsideClick) {
      onClose();
    }
  }

  onMount(() => {
    document.addEventListener('fullscreenchange', onFullscreenChange);

    window.addEventListener('keydown', handleKeyDown);
  });

  onDestroy(() => {
    document.removeEventListener('fullscreenchange', onFullscreenChange);

    window.removeEventListener('keydown', handleKeyDown);
  });
</script>

{#if open}
  <!-- Backdrop -->

  <div
    class="fixed inset-0 z-40
    bg-black/20 backdrop-blur-[2px]"
    on:click={onBackdropClick}>
  </div>

  <!-- Modal Wrapper -->

  <div
    class="fixed inset-0 z-50
    flex items-center justify-center
    pointer-events-none">
    <div
      bind:this={modalRef}
      class="pointer-events-auto
      bg-white
      border border-gray-300
      rounded-xl
      shadow-xl
      overflow-hidden
      flex flex-col"
      style="
      width:{isFullscreen ? '100vw' : width};
      height:{isFullscreen ? '100vh' : height};
      max-height:90vh;
      transform:translate3d(0px,0px,0px);
      will-change:transform;
      "
      on:click|stopPropagation>
      <!-- Header -->

      <div
        class="h-12
        px-4
        border-b border-gray-300
        bg-[#dbeaff]
        flex items-center justify-between
        cursor-move
        select-none"
        on:mousedown={onMouseDown}>
        <div
          class="font-medium
          text-gray-800
          truncate">
          {title}
        </div>

        <div class="flex items-center gap-1">
          {#if isMoved}
            <button
              class="w-8 h-8
              rounded
              hover:bg-white/50
              flex items-center justify-center"
              title="Reset Position"
              on:click={resetPosition}>
              <RefreshCw size={16} />
            </button>
          {/if}

          <button
            class="w-8 h-8
            rounded
            hover:bg-white/50
            flex items-center justify-center"
            title="App Fullscreen"
            on:click={toggleFullscreen}>
            {#if isFullscreen}
              <Shrink size={16} />
            {:else}
              <Expand size={16} />
            {/if}
          </button>

          <button
            class="w-8 h-8
            rounded
            hover:bg-white/50
            flex items-center justify-center"
            title="Browser Fullscreen"
            on:click={toggleBrowserFullscreen}>
            {#if document.fullscreenElement}
              <Minimize size={16} />
            {:else}
              <Maximize size={16} />
            {/if}
          </button>

          <button
            class="w-8 h-8
            rounded
            hover:bg-red-50
            hover:text-red-600
            flex items-center justify-center"
            title="Close"
            on:click={onClose}>
            <X size={18} />
          </button>
        </div>
      </div>

      <!-- Body -->

      <div class="flex-1 overflow-auto p-0">
        <slot />
      </div>

      <!-- Footer -->

      <div
        class="px-4 py-3
        border-t border-gray-300
        bg-gray-50
        flex justify-end gap-2">
        <button
          class="
          h-9 px-4
          text-sm
          border border-gray-300
          rounded-md
          bg-white
          hover:bg-gray-50"
          on:click={onClose}>
          {cancelText}
        </button>

        <button
          class="
          inline-flex h-9 items-center justify-center gap-2 px-4
          text-sm
          rounded-md
          text-white
          bg-blue-600
          hover:bg-blue-700
          disabled:cursor-not-allowed
          disabled:opacity-60"
          on:click={onConfirm}
          disabled={confirmDisabled}>
          {#if confirmBusy}
            <Loader2 size={15} class="animate-spin" />
          {/if}
          {confirmText}
        </button>
      </div>
    </div>
  </div>
{/if}
